import { GraphQLResult, generateClient } from "aws-amplify/api";
import { FieldInstance, FieldInstanceForResolver, FieldTemplateForResolver } from "db/definitions";
import { handleConvertSourceFieldTemplateToTarget } from "utils/convertFormGroups";
import { recordSentry } from "utils/recordSentry";
import { v4 as uuid } from "uuid";
import { db } from "../db";
import { buildFieldTemplateAsync } from "./fieldTemplateQueries";
import { deleteImageDoc, getImageDoc } from "./imageQueries";
import { captureMessage } from "@sentry/react";

interface GetFieldTemplateToUse {
	donor: FieldTemplateForResolver;
	receiver: FieldTemplateForResolver;
}

const getFieldTemplateToUse = ({ donor, receiver }: GetFieldTemplateToUse): FieldTemplateForResolver => {
	const donorVersion = donor.version?.split("_")[1];
	const receiverVersion = receiver.version?.split("_")[1];
	const donorUpdatedAt = new Date(donor.updatedAt);
	const receiverUpdatedAt = new Date(receiver.updatedAt);

	let donorDate, receiverDate;
	if (donorVersion) donorDate = new Date(donorVersion);
	if (receiverVersion) receiverDate = new Date(receiverVersion);

	if (donorDate && receiverDate) {
		if (donorDate === receiverDate) return donorUpdatedAt > receiverUpdatedAt ? donor : receiver;
		if (donorDate > receiverDate) return donor;
		return receiver;
	}

	if (donorDate && !receiverDate) return donor;
	if (!donorDate && receiverDate) return receiver;
	return donorUpdatedAt > receiverUpdatedAt ? donor : receiver;
};

/**
 * Given a raw fieldInstance, checks if it has an alt id and returns the built out field with altInstance
 * @author Eric Webb <ewebb@factorearth.com>
 * @param fieldInstance The raw fieldInstance
 * @returns The built fieldInstance
 */
async function buildAltFieldAsync(fieldInstance: FieldInstanceForResolver): Promise<FieldInstance | null> {
	// Given a raw fieldInstance, checks if it has an alt id and returns the built out field with altInstance
	// Step 1: Check if there's no altId. If not, return null
	// Step 2: Get the fieldInstance with the altId
	// Step 3: Build the field
	// Step 4: Return the built Field

	// Step 1: Check if there's no altId. If not, return null. MOST FIELDS DON'T HAVE ALT INSTANCES, ONLY LINKED FIELDS. If we were querying the dynamodb, they'd come back null
	if (!fieldInstance.altInstanceId) return null;

	// Step 2: Get the fieldInstance with the altId
	const retrievedField = await getOfflineFieldInstanceById(fieldInstance.altInstanceId);
	// Step 3: Build the field
	if (retrievedField) {
		const builtField = await buildFieldInstance(retrievedField, true);

		// Step 4: return the built Field
		return builtField;
	}
	return null;
}

export async function bulkGetAndBuildFieldInstances(ids: string[]): Promise<FieldInstance[]> {
	const fieldInstancesForResolver = await bulkRetrieveFieldInstances(ids);
	const promises = [];
	for (const fieldInstance of fieldInstancesForResolver) {
		if (fieldInstance) promises.push(buildFieldInstance(fieldInstance));
	}
	return await Promise.all(promises);
}

/**
 * Given a fieldInstance retrieved from indexedDb, finds the children properties and attaches everything, returning it. It builds out like the amplify calls return them
 * @author Eric Webb <ewebb@factorearth.com>
 * @param fieldInstance The fieldInstance we need to build out
 * @param fromAltInstance Whether or not a field is an alt instance (we need this to prevent circular queries)
 * @returns The completely built out fieldInstance
 */
export async function buildFieldInstance(fieldInstance: FieldInstanceForResolver, fromAltInstance: boolean = false): Promise<FieldInstance> {
	const [fieldTemplate, resolvedTableFields, builtAltInstance, builtImageDoc] = await Promise.all([
		buildFieldTemplateAsync(fieldInstance),
		buildTableFieldsAsync(fieldInstance.id),
		!fromAltInstance ? buildAltFieldAsync(fieldInstance) : null,
		fieldInstance.imageId ? getImageDoc(fieldInstance.imageId) : null,
	]);

	const fieldToReturn: FieldInstance = {
		...fieldInstance,
		tableFields: { items: resolvedTableFields },
		fieldTemplate,
		altInstance: builtAltInstance,
		image: builtImageDoc,
	};
	return fieldToReturn;
}

/**
 * Given a raw fieldInstance, retrieves all fieldInstances where tableField === fieldInstance.id, builds them, and returns them
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id The fieldInstance id
 * @returns An array of built tableFields
 */
async function buildTableFieldsAsync(id: string) {
	// Given a raw fieldInstance, retrieves all fieldInstances where tableField === fieldInstance.id, builds them, and returns them
	// Step 1: Get all fieldInstances where tableField === fieldInstance.id
	// Step 2: Create promise array for built tableFields
	// Step 3: For each retrieved tableField, push a promise that builds the tableField
	// Step 4: Await promises
	// Step 5: Return built tableFields

	// Step 1: Get all fieldInstances where tableField === fieldInstance.id
	const retrievedFields = await getAllTableFieldChildren(id);

	// Step 2: Create promise array for built tableFields
	const builtTableFieldPromises = [];

	// Step 3: For each retrieved tableField, push a promise that builds the tableField
	for (const tableField of retrievedFields) {
		builtTableFieldPromises.push(buildFieldInstance(tableField));
	}

	// Step 4: Await promises
	const builtTableFields = await Promise.all(builtTableFieldPromises);

	// Step 5: Return built tableFields
	return builtTableFields;
}

/**
 * Given the ids, retrieves their respective fieldInstances
 * @author Eric Webb <ewebb@factorearth.com>
 * @param ids The ids of the fieldInstances we want to retrieve
 * @returns The fieldInstances
 */
export async function bulkRetrieveFieldInstances(ids: string[]) {
	const fieldInstances = await db.fieldInstance.bulkGet(ids);
	return fieldInstances;
}

/**
 * Given a formGroup id, retrieves all fieldInstances within the formGroup
 * @author Eric Webb <ewebb@factorearth.com>
 * @param formGroupId The id of the formGroup that the fieldInstances all belong to
 * @returns All fieldInstances within a formGroup
 */
export async function bulkRetrieveFieldInstancesByFormGroup(formGroupId: string) {
	const fieldInstances = await db.fieldInstance.where("formGroup").equals(formGroupId).toArray();
	return fieldInstances;
}

/**
 * Given an array of fieldInstances, saves them to the indexedDb
 * @author Eric Webb <ewebb@factorearth.com>
 * @param documents The fieldInstance objects that we want to save to the indexedDb
 */
export async function bulkSaveFieldInstances(documents: FieldInstanceForResolver[]) {
	await db.fieldInstance.bulkPut(documents);
}

/**
 * Given the ids of some fieldInstances, deletes all of them from the indexeddb
 * @author Eric Webb <ewebb@factorearth.com>
 * @param ids The fieldInstanceIds we want to delete
 */
export async function bulkDeleteFieldInstances(ids: string[]): Promise<void> {
	await db.fieldInstance.bulkDelete(ids);
}

/**
 * Given both the donor and receiver fieldInstances, combines their notes together. I imagine this will get refactored
 * @author Eric Webb <ewebb@factorearth.com>
 * @param donorFieldInstance The "donor" fieldInstance
 * @param receiverFieldInstance The "receiver" fieldInstance
 * @returns The combined string of field notes, or null
 */
function combineFieldNotes(donorFieldInstance: FieldInstanceForResolver, receiverFieldInstance: FieldInstanceForResolver): string {
	let finalNotes = "";
	if (!donorFieldInstance.notes && !receiverFieldInstance.notes) return finalNotes;
	if (receiverFieldInstance.notes) finalNotes += receiverFieldInstance.notes;
	if (donorFieldInstance.notes) finalNotes += donorFieldInstance.notes;
	// If we combined 2 empty strings
	return finalNotes;
}

/**
 * Given an entire fieldInstance, creates it in the format the batchAddFieldInstance resolver expects it. THIS FUNCTION IS TO CREATE, NOT UPDATE EXISTING
 * @author Eric Webb <ewebb@factorearth.com>
 * @param field The fieldObject
 * @returns The createdFieldInstance
 */
export function createFieldForBatchResolver(field: Pick<FieldInstance, "formGroup" | "group"> & Partial<FieldInstance>): FieldInstanceForResolver {
	const timestamp = new Date().toISOString();
	// REVISION_CHANGE
	const fieldToSave: FieldInstanceForResolver = {
		id: field.id || uuid(),
		authors: field.authors || [],
		lastAuthor: field.lastAuthor || "",
		createdAt: field.createdAt || timestamp,
		updatedAt: field.updatedAt || field.createdAt || timestamp,
		editing: "",
		fieldTemplateId: field.fieldTemplateId || null,
		formGroup: field.formGroup,
		fieldsOrder: field.fieldsOrder || null,
		group: field.group,
		notes: field.notes || "",
		// revisions: field.revisions || [],
		value: field.value || "",
	};
	// Resolver throws a hissy fit if the tableField is null cause it's indexable
	if (field.tableField) fieldToSave.tableField = field.tableField;
	// Resolver throws a hissy fit if the formInstance is null cause it's indexable
	if (field.formInstance) fieldToSave.formInstance = field.formInstance;
	// Resolver throws a hissy fit if the altInstanceid is null cause it's indexable
	if (field.altInstanceId) fieldToSave.altInstanceId = field.altInstanceId;
	// Guess what?
	if (field.imageId) fieldToSave.imageId = field.imageId;
	return fieldToSave;
}

/**
 * Given a donor and reciever fieldInstance, merges all of the information based off of the "updatedAt" time
 * @author Eric Webb <ewebb@factorearth.com>
 * @param donorFieldInstance The "donor" fieldInstance
 * @param receiverFieldInstance The "reciever" field instance
 * @param fieldTemplates A map of all of the fieldTemplates we saved in the merge process
 * @param formTemplates A map of all of the formTemplates we saved in the merge process
 * @returns A fieldInstance document, ready to save online/offline
 */
export function createMergedFieldForBatchResolver(
	donorFieldInstance: FieldInstanceForResolver,
	receiverFieldInstance: FieldInstanceForResolver,
	fieldTemplates: Map<string, FieldTemplateForResolver>,
): FieldInstanceForResolver {
	// On the off-chance a field doesn't have an updatedAt, this would make it the current time. Should be useless
	const timeStamp = new Date();
	const leftUpdatedAt = donorFieldInstance.updatedAt ? new Date(donorFieldInstance.updatedAt) : timeStamp;
	const rightUpdatedAt = receiverFieldInstance.updatedAt ? new Date(receiverFieldInstance.updatedAt) : timeStamp;

	// const revisionsToUse = getRevisionsArray(donorFieldInstance, receiverFieldInstance);
	// fieldsOrder merge: use a set-union by cell-ID overlap so that rows added offline on
	// separate devices are all preserved after sync. The winner's order is kept as the
	// primary sequence; any loser rows whose cell IDs do not appear anywhere in the winner's
	// rows are appended. Matching by cell-ID overlap (rather than exact string equality)
	// means that old corrupted row strings (missing hidden-column IDs from the pre-b1e4bf1e
	// sort bug) and their corrected counterparts are still recognised as the same row and
	// won't be duplicated. If neither side has a fieldsOrder (regular non-table field),
	// the result is an empty array, which is what createFieldForBatchResolver stores as null.
	const leftFieldsOrder = donorFieldInstance.fieldsOrder ?? [];
	const rightFieldsOrder = receiverFieldInstance.fieldsOrder ?? [];
	let fieldOrderToUse: string[];
	if (leftFieldsOrder.length > 0 || rightFieldsOrder.length > 0) {
		const winner = leftUpdatedAt >= rightUpdatedAt ? leftFieldsOrder : rightFieldsOrder;
		const loser = leftUpdatedAt >= rightUpdatedAt ? rightFieldsOrder : leftFieldsOrder;
		const winnerCellIds = new Set(winner.flatMap((row) => row.split(",")));
		const newLoserRows = loser.filter((row) => !row.split(",").some((cellId) => winnerCellIds.has(cellId)));
		fieldOrderToUse = [...winner, ...newLoserRows];
	} else {
		fieldOrderToUse = [];
	}
	const authorsToUse = getUniqueStringArray(donorFieldInstance.authors, receiverFieldInstance.authors);
	const valueAndSourceFieldTemplateId = getValueAndSourceFieldTemplateId(donorFieldInstance, receiverFieldInstance);
	const notesToUse = combineFieldNotes(donorFieldInstance, receiverFieldInstance);

	const donor = fieldTemplates.get(donorFieldInstance.fieldTemplateId || "");
	const receiver = fieldTemplates.get(receiverFieldInstance.fieldTemplateId || "");

	let fieldTemplateToUse: FieldTemplateForResolver | undefined;
	// The id we will fall back to when the template map can't tell us which template to use. It must never be null
	// when either side carried an id: a merge that strips fieldTemplateId leaves the field unrenderable and makes
	// verifyFormGroupInstances believe the whole form is missing (which is how duplicate field sets were created).
	const fallbackFieldTemplateId: string | null =
		valueAndSourceFieldTemplateId.sourceFieldTemplateId || receiverFieldInstance.fieldTemplateId || donorFieldInstance.fieldTemplateId || null;
	if (donor && receiver) {
		fieldTemplateToUse = getFieldTemplateToUse({ donor, receiver });
	} else if (donor && !receiver) {
		// In case somehow one side has a fieldTemplate and the other doesn't
		fieldTemplateToUse = donor;
		captureMessage(`FieldInstance ${donorFieldInstance.id} is missing fieldTemplateId`);
	} else if (!donor && receiver) {
		// In case somehow one side has a fieldTemplate and the other doesn't
		fieldTemplateToUse = receiver;
		captureMessage(`FieldInstance ${receiverFieldInstance.id} is missing fieldTemplateId`);
	} else if (fallbackFieldTemplateId) {
		// Neither side's template is in the map we were handed (e.g. the template fetch for this sync came back empty).
		// Keep the id the instances already carry rather than nulling it.
		captureMessage(
			`FieldInstance ${receiverFieldInstance.id} merged without its field template in the merge map; kept fieldTemplateId ${fallbackFieldTemplateId} (donor ${donorFieldInstance.fieldTemplateId}, receiver ${receiverFieldInstance.fieldTemplateId})`,
		);
	}

	const fieldInstanceToCreate: FieldInstanceForResolver = {
		id: receiverFieldInstance.id, // Won't change
		authors: authorsToUse,
		lastAuthor: leftUpdatedAt > rightUpdatedAt ? donorFieldInstance.lastAuthor : receiverFieldInstance.lastAuthor,
		createdAt: receiverFieldInstance.createdAt || donorFieldInstance.createdAt || new Date().toISOString(), // Tries both times, worst case is right now
		// We can compare Date objects, so I had to parse the ISO strings to dates, now we have to go back now that we've done the comparison
		updatedAt: leftUpdatedAt > rightUpdatedAt ? leftUpdatedAt.toISOString() : rightUpdatedAt.toISOString(),
		editing: "", // SHOULD be empty
		fieldTemplateId: fieldTemplateToUse?.id || fallbackFieldTemplateId,
		formGroup: receiverFieldInstance.formGroup || donorFieldInstance.formGroup, // Doesn't change
		fieldsOrder: fieldOrderToUse,
		group: receiverFieldInstance.group || donorFieldInstance.group, // Won't change
		notes: notesToUse,
		// revisions: revisionsToUse,
		value: valueAndSourceFieldTemplateId.value,
		// mergeValue: mergeValues
	};
	// Resolver throws a hissy fit if the tableField is null cause it's indexable
	if (donorFieldInstance.tableField) fieldInstanceToCreate.tableField = donorFieldInstance.tableField;
	if (receiverFieldInstance.tableField) fieldInstanceToCreate.tableField = receiverFieldInstance.tableField;
	// Resolver throws a hissy fit if the formInstance is null cause it's indexable
	if (donorFieldInstance.formInstance) fieldInstanceToCreate.formInstance = donorFieldInstance.formInstance;
	if (receiverFieldInstance.formInstance) fieldInstanceToCreate.formInstance = receiverFieldInstance.formInstance;
	// Resolver throws a hissy fit if the altInstanceid is null cause it's indexable
	if (donorFieldInstance.altInstanceId) fieldInstanceToCreate.altInstanceId = donorFieldInstance.altInstanceId;
	if (receiverFieldInstance.altInstanceId) fieldInstanceToCreate.altInstanceId = receiverFieldInstance.altInstanceId;
	// Another Index. Guess what?
	if (donorFieldInstance.imageId) fieldInstanceToCreate.imageId = donorFieldInstance.imageId;
	if (receiverFieldInstance.imageId) fieldInstanceToCreate.imageId = receiverFieldInstance.imageId;

	if (fieldTemplateToUse && valueAndSourceFieldTemplateId.sourceFieldTemplateId) {
		if (valueAndSourceFieldTemplateId.sourceFieldTemplateId !== fieldTemplateToUse.id) {
			const fakeFormInstance = {
				id: receiverFieldInstance,
				group: receiverFieldInstance.group,
				formGroup: receiverFieldInstance.formGroup,
			};
			const sourceFieldTemplate = fieldTemplates.get(valueAndSourceFieldTemplateId.sourceFieldTemplateId);
			const convertedFieldInstance = handleConvertSourceFieldTemplateToTarget(
				fieldInstanceToCreate,
				sourceFieldTemplate,
				fieldTemplateToUse,
				fakeFormInstance,
			);
			return convertedFieldInstance;
		}
	}

	return fieldInstanceToCreate;
}

/**
 * Deletes a fieldInstance as well as the table fields and checks if the altInstances are there and can be safely deleted
 * @author Eric Webb <ewebb@factorearth.com>
 * @param {Object} fieldInstance The fieldInstance we want to delete
 * @returns Array of statuses
 */
export async function deleteFieldInstance(fieldInstance: FieldInstanceForResolver): Promise<void> {
	// Delete the fieldInstance and all child fields
	// Step 1: Create promise to delete all Table fields
	// Step 2: Check if there's a linked field. If there is, find all fieldInstances that have the same alt instance
	// Step 3: Determine if any of the fields with that linked field are used outside of the formInstance
	// Step 4: If it isn't, push a promise to delete it
	// Step 5: Delete the fieldInstance
	// Step 6: Await the promises

	const deletePromises = [];
	// Hey future person! If we need to support linked tableFields, then this will have to change, because then we couldn't just delete all tableFields I think
	// Step 1: Create promise to delete all Table fields
	deletePromises.push(deleteFieldInstancesByTableFieldIndex(fieldInstance.id));

	if (fieldInstance.altInstanceId) {
		// Step 2: Check if there's a linked field. If there is, find all fieldInstances that have the same alt instance
		const allFieldsThatHaveThisAltInstance = await getAllFieldInstancesByAltInstanceIdIndex(fieldInstance.altInstanceId);

		// Step 3: Determine if any of the fields with that linked field are used outside of the formInstance
		const linkedBetweenFormInstances = allFieldsThatHaveThisAltInstance.some((field) => field.formInstance !== fieldInstance.formInstance);
		if (!linkedBetweenFormInstances) {
			// Step 4: If it isn't, push a promise to delete it
			deletePromises.push(deleteFieldInstanceById(fieldInstance.altInstanceId));
		}
	}
	if (fieldInstance.imageId) {
		deletePromises.push(deleteImageDoc(fieldInstance.imageId));
	}

	// Step 5: Delete the fieldInstance
	deletePromises.push(db.fieldInstance.delete(fieldInstance.id));

	// Step 6: Await the promises
	await Promise.all(deletePromises);
}

/**
 * Given an array of fieldInstances, deletes them. As far as I can think, this should only ever be used for deleting rows from a table, as the only other way fieldInstances should
 * be deleted is by deleting the formInstance.
 * @author Eric Webb <ewebb@factorearth.com>
 * @param fieldInstances The fieldInstances we want to delete
 */
export async function deleteFieldInstances(fieldInstances: FieldInstanceForResolver[]): Promise<void> {
	// Loop through the fields, and delete them
	// Step 1: Create a promise array so we can do it in parallel
	// Step 2: For each field, push a promise to delete it
	// Step 3: Await all promises

	// Step 1: Create a promise array so we can do it in parallel
	const deletePromises = [];
	for (const fieldInstance of fieldInstances) {
		// Step 2: For each field, push a promise to delete it
		deletePromises.push(deleteFieldInstance(fieldInstance));
	}

	// Step 3: Await all promises
	await Promise.all(deletePromises);
}

/**
 * Given the id of the owner formGroup, queries all fieldInstances by it and deletes them
 * @author Eric Webb <ewebb@factorearth.com>
 * @param formGroupId The owner formGroup id
 */
export async function deleteFieldInstancesByFormGroupIndex(formGroupId: string): Promise<void> {
	await db.fieldInstance.where("formGroup").equals(formGroupId).delete();
}

/**
 * Given a fieldInstance id, deletes the document from the database
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id The id of the field we want to delete
 */
async function deleteFieldInstanceById(id: string): Promise<void> {
	await db.fieldInstance.delete(id);
}

/**
 * Given the id of a parent field, deletes all children fieldInstances within the table
 * @author Eric Webb <ewebb@factorearth.com>
 * @param tableField The id of the parent field
 */
async function deleteFieldInstancesByTableFieldIndex(tableField: string): Promise<void> {
	await db.fieldInstance.where("tableField").equals(tableField).delete();
}

/**
 * Given an altInstance id, grabs all fieldInstances which have that altInstanceId
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id The id of the altInstance field
 * @returns All fields where their altInstanceId is the id passed to this function
 */
async function getAllFieldInstancesByAltInstanceIdIndex(id: string): Promise<FieldInstanceForResolver[]> {
	const fields = await db.fieldInstance.where("altInstanceId").equals(id).toArray();
	return fields;
}

/**
 * Given the formInstance id, gets all fieldInstances within the formInstance
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id The id of the formInstance
 * @returns All fieldInstances within the formInstance
 */
export async function getAllFieldsByFormInstanceId(id: string): Promise<FieldInstanceForResolver[]> {
	const fieldInstances = await db.fieldInstance.where("formInstance").equals(id).toArray();
	return fieldInstances;
}

/**
 * Given the id of the parent field, grabs all children fields within a table
 * @author Eric Webb <ewebb@factorearth.com>
 * @param tableFieldId The id of the parent field
 * @returns All fieldInstances within the table
 */
export async function getAllTableFieldChildren(tableFieldId: string): Promise<FieldInstanceForResolver[]> {
	const tableFields = await db.fieldInstance.where("tableField").equals(tableFieldId).toArray();
	return tableFields;
}

/**
 * Given the id of a fieldInstance, queries and returns the fieldInstance doc
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id The id of the fieldInstance
 * @returns The fieldInstance document
 */
export async function getFieldInstanceById(id: string): Promise<FieldInstanceForResolver | undefined> {
	const field = await db.fieldInstance.get(id);
	return field;
}

/**
 * Given the offline field and the online field, determines what the fieldsOrder value should be
 * @author Eric Webb <ewebb@factorearth.com>
 * @param {Object} donorStrings The offline fieldInstance
 * @param {Object} receiverStrings The online fieldInstance
 * @returns The value to use for the fieldsOrder
 */
function getUniqueStringArray(donorStrings: string[] | null, receiverStrings: string[] | null): string[] {
	const fieldsOrderSet: Set<string> = new Set();
	if (donorStrings) {
		if (receiverStrings) {
			for (const field of receiverStrings) {
				fieldsOrderSet.add(field);
			}
		}
		for (const field of donorStrings) {
			fieldsOrderSet.add(field);
		}
	}
	return [...fieldsOrderSet];
}

/**
 * Given the tableField id, gets it and all of its children tableFields
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id The id of the fieldInstance
 * @returns The table field with its tableFields
 */
export async function getTableField(id: string): Promise<FieldInstance | undefined> {
	const [field, tableFields] = await Promise.all([getFieldInstanceById(id), buildTableFieldsAsync(id)]);
	if (field) {
		const fieldInstance: FieldInstance = { ...field, tableFields: { items: tableFields } };
		return fieldInstance;
	}
	return field;
}

/**
 * Given the id of a fieldInstance, queries and retrieves the document
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id The id of the fieldInstance we want to retrieve
 * @returns The retrieved fieldInstance document
 */
async function getOfflineFieldInstanceById(id: string): Promise<FieldInstanceForResolver | undefined> {
	const retrievedField = await db.fieldInstance.get(id);
	return retrievedField;
}

/**
 * Queries the count of all FieldInstances, multiplies the count by the average size, and returns that value
 * @author Eric Webb <ewebb@factorearth.com>
 * @returns The filesize, in bytes, of all fieldInstances
 */
export async function getTotalFieldInstanceDiskSize() {
	const number = await db.fieldInstance.count();
	const AVERAGE_DOCUMENT_SIZE = 406.56;
	return number * AVERAGE_DOCUMENT_SIZE;
}

/**
 * Given the offline field and the online field, determines what value we should be using when we do the update
 * @author Eric Webb <ewebb@factorearth.com>
 * @param donorFieldInstance The offline fieldInstance
 * @param receiverFieldInstance The online fieldInstance
 * @returns The value we want to use when we update the online document
 */
function getValueAndSourceFieldTemplateId(
	donorFieldInstance: FieldInstanceForResolver,
	receiverFieldInstance: FieldInstanceForResolver,
): { value: string; sourceFieldTemplateId: string | null } {
	const timeStamp = new Date();
	const leftDate = donorFieldInstance.updatedAt ? new Date(donorFieldInstance.updatedAt) : timeStamp;
	const rightDate = receiverFieldInstance.updatedAt ? new Date(receiverFieldInstance.updatedAt) : timeStamp;

	return leftDate >= rightDate
		? { value: donorFieldInstance.value, sourceFieldTemplateId: donorFieldInstance.fieldTemplateId }
		: { value: receiverFieldInstance.value, sourceFieldTemplateId: receiverFieldInstance.fieldTemplateId };
}

/**
 * Given the id of the fieldInstance, updates its touched value to 1. This is used for updating image docs, and setting the parent field to touched so merging works
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id The id of the fieldInstance
 */
export async function setFieldInstanceToTouched(id: string) {
	if (id) {
		await db.fieldInstance.update(id, { touched: 1, updatedAt: new Date().toISOString() });
	}
}

/**
 * Given data, updates the fieldInstance locally
 * @param fieldInstance The data we want to update the fieldInstance with. Id is required, everything else is technically optional
 */
export async function updateFieldInstance(fieldInstance: Pick<FieldInstanceForResolver, "id"> & Partial<FieldInstanceForResolver>): Promise<boolean> {
	// .update changes the properties we specify, but will leave the other properties alone
	try {
		await db.fieldInstance.update(fieldInstance.id, fieldInstance as any);
		if (fieldInstance.formInstance) {
			const updateFormInstanceData = {
				id: fieldInstance.formInstance,
				updatedAt: fieldInstance.updatedAt || new Date().toISOString(),
			};
			await db.formInstance.update(fieldInstance.formInstance, updateFormInstanceData as any);
		}
		if (fieldInstance.formGroup) {
			const updateFormGroupData = {
				id: fieldInstance.formGroup,
				group: fieldInstance.group,
				updatedAt: fieldInstance.updatedAt || new Date().toISOString(),
				lastUpdatedBy: fieldInstance.lastAuthor || fieldInstance.editing || "",
			};
			await db.formGroup.update(fieldInstance.formGroup, updateFormGroupData as any);
		}
		return true;
	} catch (err) {
		recordSentry({ error: err, msg: `Error updating fieldInstance ${fieldInstance.id}: ` });
		return false;
	}
}
